import { breakpoint } from '@/helpers/BreakpointHelper';
import { isBrowser } from '@/helpers/BrowserHelper';
import React, { FC, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

export interface ModalProps {
  isOpen?: boolean;
  // 모달 바깥쪽 누를시 닫을수 있는지 여부
  dismissable?: boolean;
  // 닫힘요청시 콜백
  onRequestClose: () => void;
  maxWidth?: string;
  style?: any;
  color?: string;
  zIndex?: string;
}

const backdropFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 0.5;
  }
`;

const backdropFadeOut = keyframes`
  from {
    opacity: 0.5;
  }
  to {
    opacity: 0;
  }
`;

const slideIn = keyframes`
  from {
    top: -20px; 
  }
  to {
    top: 0;
  }
`;

const slideOut = keyframes`
  from {
    top: 0;
  }
  to {
    top: -20px;
  }
`;

const ModalContainer = styled.div<{ isOpen: boolean; zIndex?: string }>`
  display: ${({ isOpen }) => (isOpen ? `block` : `none`)};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  z-index: ${({ zIndex }) => zIndex || '100'};
`;

const ModalWrapper = styled.div`
  display: flex;
  justify-content: center;
  min-height: 100%;
  padding: 3rem 1rem;
  box-sizing: border-box;

  ${breakpoint(`mobile`)} {
    padding: 20px;
  }
`;

const ModalBackdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  background-color: #000;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  opacity: 0.5;
  animation: ${({ isOpen }) => (isOpen ? backdropFadeIn : backdropFadeOut)}
    0.15s linear;
`;
export const ModalDialog = styled.div<{ isOpen: boolean; maxWidth?: string }>`
  position: relative;
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || `880px`};
  background-color: #fff;
  z-index: 1;

  // 화면보다 작으면 세로 가운데, 크면 위에서부터 시작
  display: flex;
  flex-direction: column;
  margin: auto 0;

  animation: ${({ isOpen }) => (isOpen ? slideIn : slideOut)} 0.15s linear;
`;

const Modal: FC<ModalProps> = ({
  children,
  isOpen = true,
  dismissable = false,
  onRequestClose,
  maxWidth,
  style,
  color,
  ...props
}) => {
  const onBackdropClick = useCallback(() => {
    if (dismissable) {
      onRequestClose();
    }
  }, [dismissable, onRequestClose]);

  if (!isBrowser) {
    return null;
  }

  // createPortal로 document.body에 append
  return createPortal(
    <ModalContainer isOpen={isOpen} style={style} {...props}>
      <ModalWrapper>
        <ModalDialog isOpen={isOpen} maxWidth={maxWidth}>
          {children}
        </ModalDialog>
      </ModalWrapper>
      <ModalBackdrop isOpen={isOpen} onClick={onBackdropClick} />
    </ModalContainer>,
    document.body,
  );
};

export default Modal;
